<template>
  <div>
    <section
      class="py-32 px-6 bg-[var(--cream)]"
    >
      <div class="max-w-6xl mx-auto">
        <div class="text-center mb-20 space-y-4 fade-in-content">
          <h1 class="text-5xl md:text-6xl font-bold italic">News, Reports & Media</h1>
          <p class="text-xl text-[var(--moss)] italic max-w-2xl mx-auto">
            Stories of transformation, research insights, and community impact.
          </p>
        </div>

        <div class="space-y-24">
          <!-- Case Study 1 - Video -->
          <div class="case-study-item fade-in-item">
            <div class="grid md:grid-cols-2 gap-12 items-center">
              <div class="order-2 md:order-1">
                <h2 class="text-3xl font-bold italic mb-4">
                  Community Healing Journey
                </h2>
                <p
                  class="text-lg text-[var(--moss)] italic leading-relaxed mb-6"
                >
                  Watch how peer-led support groups have transformed lives in
                  our community, creating spaces for authentic connection and
                  healing.
                </p>
                <button
                  type="button"
                  @click="openVideoModal"
                  class="btn-organic bg-[var(--terracotta)] text-white px-8 py-3 font-bold inline-flex items-center gap-2"
                >
                  Watch Reel
                  <span class="material-symbols-outlined">play_circle</span>
                </button>
              </div>
              <div class="order-1 md:order-2 relative">
                <button
                  type="button"
                  @click="openVideoModal"
                  class="w-full aspect-video rounded-[40%_60%_30%_70%/60%_30%_70%_40%] overflow-hidden border-4 border-white shadow-2xl case-study-blob flex items-center justify-center relative group cursor-pointer bg-[var(--forest)]/10"
                  aria-label="Open Instagram video in modal"
                >
                  <div
                    class="absolute inset-0 bg-gradient-to-tr from-black/60 via-black/30 to-transparent pointer-events-none"
                  ></div>
                  <div
                    class="relative z-10 flex flex-col items-center justify-center text-center px-4"
                  >
                    <span
                      class="material-symbols-outlined text-5xl md:text-6xl text-white drop-shadow-lg"
                    >
                      play_circle
                    </span>
                    <span
                      class="mt-3 text-sm md:text-base font-semibold text-white drop-shadow-lg"
                    >
                      Watch on Instagram
                    </span>
                  </div>
                </button>
              </div>
            </div>
          </div>

          <!-- Case Study 2 - Article -->
          <div class="case-study-item fade-in-item">
            <div class="grid md:grid-cols-2 gap-12 items-center">
              <div class="relative">
                <div
                  class="aspect-[4/3] rounded-[60%_40%_70%_30%/50%_60%_40%_50%] overflow-hidden border-4 border-white shadow-2xl case-study-blob lazy-image-container"
                >
                  <div
                    class="w-full h-full bg-cover bg-center lazy-image"
                    :data-src="caseStudies[1].image"
                  ></div>
                </div>
              </div>
              <div>
                <h2 class="text-3xl font-bold italic mb-4">
                  Empowering Young Minds with Nadra Aley
                </h2>
                <p
                  class="text-lg text-[var(--moss)] italic leading-relaxed mb-6"
                >
                  We were honoured to have Nadra Aley, a certified and
                  internationally accredited life coach and founder of the Safe
                  Haven Wellness Group, engage our Year 7–9 students in a series
                  of impactful mental health sessions.
                </p>
                <a
                  :href="caseStudies[1].url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="btn-organic bg-[var(--terracotta)] text-white px-8 py-3 font-bold inline-flex items-center gap-2"
                >
                  Read Article
                  <span class="material-symbols-outlined">article</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Video Modal -->
    <Transition name="modal">
      <div
        v-if="showVideoModal"
        @click="closeVideoModal"
        class="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      >
        <div
          @click.stop
          class="relative w-full max-w-sm md:max-w-md bg-[var(--cream)] rounded-2xl shadow-2xl overflow-hidden"
        >
          <!-- Close button -->
          <button
            @click="closeVideoModal"
            class="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 hover:bg-black/70 text-white transition-all cursor-pointer"
            aria-label="Close video"
          >
            <span class="material-symbols-outlined text-xl">close</span>
          </button>
          <!-- Instagram embed (portrait layout) -->
          <div
            class="w-full flex items-center justify-center bg-black p-4 md:p-6"
          >
            <div
              class="w-full max-w-xs md:max-w-sm aspect-[9/16] flex items-center justify-center"
            >
              <blockquote
                class="instagram-media"
                data-instgrm-captioned
                data-instgrm-permalink="https://www.instagram.com/reel/C_zwO03IIn9/?utm_source=ig_embed&amp;utm_campaign=loading"
                data-instgrm-version="14"
                style="
                  background: #fff;
                  border: 0;
                  border-radius: 12px;
                  box-shadow: 0 0 1px 0 rgba(0, 0, 0, 0.5),
                    0 1px 10px 0 rgba(0, 0, 0, 0.15);
                  margin: 0;
                  max-width: 100%;
                  min-width: 0;
                  padding: 0;
                  width: 100%;
                  height: 100%;
                "
              >
                <a
                  href="https://www.instagram.com/reel/C_zwO03IIn9/?utm_source=ig_embed&amp;utm_campaign=loading"
                  target="_blank"
                  rel="noopener noreferrer"
                  style="
                    display: block;
                    width: 100%;
                    height: 100%;
                    text-decoration: none;
                  "
                >
                  View this post on Instagram
                </a>
              </blockquote>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import { onMounted, ref } from "vue";

export default {
  name: "CaseStudiesPage",
  setup() {
    const showVideoModal = ref(false);

    // Case studies data
    const caseStudies = ref([
      {
        type: "video",
        title: "Community Healing Journey",
        url: "https://www.instagram.com/reel/C_zwO03IIn9/?utm_source=ig_embed&amp;utm_campaign=loading",
      },
      {
        type: "article",
        title: "Empowering Young Minds with Nadra Aley",
        image:
          "https://scontent.fnbo9-1.fna.fbcdn.net/v/t39.30808-6/571034183_1356838696144976_467930831598456776_n.jpg?stp=dst-jpg_s590x590_tt6&_nc_cat=100&ccb=1-7&_nc_sid=13d280&_nc_ohc=eiNhsYSU3uoQ7kNvwF4GL5i&_nc_oc=AdkhUYUjeR3UN1EMupdHFQmyrptiDiCqrO79rRLxZ0cP2abDhkUpK9EV23jC4lC5VJA&_nc_zt=23&_nc_ht=scontent.fnbo9-1.fna&_nc_gid=S5HqA6_jQc2ee_-i3MxiKw&_nc_ss=8&oh=00_Afy57o68FeUSovVznmBo-uFaQOspedn8_I22OxGCsplc6g&oe=69BDDF97",
        url: "https://web.facebook.com/NJAnairobi/posts/-empowering-young-minds-with-nadra-aleywe-were-honoured-to-have-nadra-aley-a-cer/1356838806144965/?_rdc=1&_rdr#",
      },
    ]);

    // Lazy load images with fade-in
    const setupLazyLoading = () => {
      const imageContainers = document.querySelectorAll(
        ".lazy-image-container",
      );
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const container = entry.target;
              const imgDiv = container.querySelector(".lazy-image");
              if (imgDiv && !imgDiv.classList.contains("loaded")) {
                const imageSrc = imgDiv.getAttribute("data-src");
                if (imageSrc) {
                  const img = new Image();
                  img.onload = () => {
                    imgDiv.style.backgroundImage = `url('${imageSrc}')`;
                    imgDiv.classList.add("loaded");
                    container.classList.add("fade-in");
                  };
                  img.src = imageSrc;
                }
              }
              observer.unobserve(container);
            }
          });
        },
        {
          rootMargin: "50px",
        },
      );

      imageContainers.forEach((container) => observer.observe(container));
    };

    // Staggered fade-in for content and items
    const setupSectionFadeIn = () => {
      // Observer for main content containers
      const contentElements = document.querySelectorAll(".fade-in-content");
      let visibleContentCount = 0;

      const contentObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (
              entry.isIntersecting &&
              !entry.target.classList.contains("fade-in-visible")
            ) {
              const delay = visibleContentCount * 150;
              setTimeout(() => {
                entry.target.classList.add("fade-in-visible");
              }, delay);
              visibleContentCount++;
              contentObserver.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: "-10% 0px -10% 0px",
          threshold: 0.1,
        },
      );

      contentElements.forEach((content) => {
        contentObserver.observe(content);
      });

      // Observer for individual items (cards, columns, etc.)
      const itemElements = document.querySelectorAll(".fade-in-item");
      let visibleItemCount = 0;

      const itemObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (
              entry.isIntersecting &&
              !entry.target.classList.contains("fade-in-visible")
            ) {
              const delay = visibleItemCount * 100; // 100ms delay between items for faster stagger
              setTimeout(() => {
                entry.target.classList.add("fade-in-visible");
              }, delay);
              visibleItemCount++;
              itemObserver.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: "-5% 0px -5% 0px", // Trigger earlier for items
          threshold: 0.1,
        },
      );

      itemElements.forEach((item) => {
        itemObserver.observe(item);
      });
    };

    // Video modal functions
    const openVideoModal = () => {
      showVideoModal.value = true;
      document.body.style.overflow = "hidden"; // Prevent background scrolling

      // Ensure Instagram embeds are processed when modal opens
      if (
        typeof window !== "undefined" &&
        window.instgrm &&
        window.instgrm.Embeds &&
        typeof window.instgrm.Embeds.process === "function"
      ) {
        setTimeout(() => {
          window.instgrm.Embeds.process();
        }, 0);
      }
    };

    const closeVideoModal = () => {
      showVideoModal.value = false;
      document.body.style.overflow = ""; // Restore scrolling
    };

    onMounted(() => {
      // Setup lazy loading
      setupLazyLoading();

      // Setup section fade-in animations
      setupSectionFadeIn();
    });

    return {
      showVideoModal,
      openVideoModal,
      closeVideoModal,
      caseStudies,
    };
  },
};
</script>

<style scoped>
/* Staggered fade-in content */
.fade-in-content {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
}

.fade-in-content.fade-in-visible {
  opacity: 1;
  transform: translateY(0);
}

/* Staggered fade-in items */
.fade-in-item {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
}

.fade-in-item.fade-in-visible {
  opacity: 1;
  transform: translateY(0);
}

/* Lazy loading fade-in */
.lazy-image-container {
  opacity: 0;
  transition: opacity 0.8s ease-in-out;
}

.lazy-image-container.fade-in {
  opacity: 1;
}

.lazy-image {
  opacity: 0;
  transition: opacity 0.8s ease-in-out;
}

.lazy-image.loaded {
  opacity: 1;
}

/* Case studies */
.case-study-item {
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
}

.case-study-item.fade-in-visible {
  opacity: 1;
  transform: translateY(0);
}

.case-study-blob {
  transition: border-radius 0.5s ease-in-out;
}

.case-study-item:hover .case-study-blob {
  border-radius: 50% 50% 45% 55% / 60% 60% 40% 40% !important;
}

/* Video modal transitions */
.modal-enter-active {
  transition: opacity 0.3s ease-out;
}

.modal-leave-active {
  transition: opacity 0.3s ease-in;
}

.modal-enter-active .relative {
  transition: transform 0.3s ease-out, opacity 0.3s ease-out;
}

.modal-leave-active .relative {
  transition: transform 0.3s ease-in, opacity 0.3s ease-in;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.9);
  opacity: 0;
}
</style>
